import { useKeycloak } from "@react-keycloak/web";
import { useState, useRef, useEffect } from 'react';
import { IoLogOutOutline, IoCreateOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { Avatar } from './Avatar';
import { getBadgeTailwindClasses } from '../styles/designSystem';
import { useCurrentUser, useCurrentUserStore } from '../state/currentUser';

const sidebarFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

interface SidebarUserAreaProps {
  collapsed: boolean;
}

export const SidebarUserArea = ({ collapsed }: SidebarUserAreaProps) => {
  const { keycloak } = useKeycloak();
  const { profile } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    useCurrentUserStore.getState().clear();
    keycloak.logout({ redirectUri: window.location.origin });
  };

  const firstName = profile?.first_name || '';
  const lastName = profile?.last_name || '';
  const email = profile?.email || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'User';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center rounded-md transition-all duration-150 hover:bg-[#eaeaea] ${
          collapsed ? 'justify-center py-1.5' : 'gap-2.5 px-2 py-1.5'
        }`}
        title={collapsed ? fullName : undefined}
      >
        <Avatar
          firstName={firstName}
          lastName={lastName}
          email={email}
          src={profile?.photo_url}
          size={28}
        />
        {!collapsed && (
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[13px] font-medium text-[#171717] truncate" style={sidebarFont}>{fullName}</div>
            <div className="text-[11px] text-[#888] truncate" style={sidebarFont}>{email}</div>
          </div>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute bottom-full mb-2 w-[260px] bg-white rounded-xl border border-[#eaeaea] shadow-[0_4px_12px_rgba(0,0,0,0.08)] py-1 z-50 ${
            collapsed ? 'left-1/2 -translate-x-1/2' : 'left-0'
          }`}
          style={sidebarFont}
        >
          <div className="px-3 py-1.5 text-[11px] text-[#888] uppercase tracking-wider font-medium">Profile <span className={getBadgeTailwindClasses('dev')}>dev</span></div>

          <div className="px-3 py-2">
            <div className="flex items-center gap-3">
              <Avatar
                firstName={firstName}
                lastName={lastName}
                email={email}
                src={profile?.photo_url}
                size={36}
              />
              <div className="min-w-0">
                <div className="text-[13px] text-[#171717] font-medium truncate">{fullName}</div>
                <div className="text-[12px] text-[#888] truncate">{email}</div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#eaeaea] mt-1">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-1.5 text-left hover:bg-[#fafafa] flex items-center gap-2.5 text-[13px] text-[#666] hover:text-[#171717] transition-colors duration-100"
            >
              <IoCreateOutline className="text-[#888] w-3.5 h-3.5" />
              View Profile
            </Link>
            <button
              onClick={handleLogout}
              className="w-full px-3 py-1.5 text-left hover:bg-[#fafafa] flex items-center gap-2.5 text-[13px] text-[#666] hover:text-[#171717] transition-colors duration-100"
            >
              <IoLogOutOutline className="text-[#888] w-3.5 h-3.5" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
